/* eslint-disable no-param-reassign */
import _ from 'underscore';

import { generateGuid, cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import {
  getComponentByType,
  getDataSourceByKeys,
  getDataSourceByKey,
  getWebsiteSourceDataByKey,
} from './site-config-reader';
import {
  DATA_SOURCE_KEY_MAPPING,
  COMPONENT_TYPE_MAPPING,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
  DATA_SOURCE_TO_WEBSITE_SOURCE_DATA_MAPPING,
} from './site-config-structure';
import { mapHoursToWSDFormat, getDefaultHoursConfig } from './time-util';
import { linkNameToModel } from './social-link-util';
import { getImageUrlById } from './image/site-config-image-reader';
import { getComponentsPilotMapping } from './component-utils';

export const convertCallToActionId = (callToActions) => {
  _.each(callToActions, (action) => {
    action.CallToActionId = action.CallToActionId || action.Id;
    delete action.Id;
  });
};

export const updateWebsiteSourceDataWithBasicData = (siteConfig, smartPageData, key, overwrite) => {
  const wsdKey = DATA_SOURCE_TO_WEBSITE_SOURCE_DATA_MAPPING[key];

  if (!wsdKey || _.isEmpty(siteConfig) || _.isEmpty(smartPageData)) {
    return;
  }

  siteConfig.websiteSourceData = siteConfig.websiteSourceData || {};

  const shouldSetValue = overwrite ||
    (!overwrite && _.isEmpty(siteConfig.websiteSourceData[wsdKey]));

  if (shouldSetValue) {
    if (wsdKey === WEBSITE_SOURCE_DATA_KEY_MAPPING.Email && !_.isEmpty(smartPageData[key])) {
      [siteConfig.websiteSourceData[wsdKey]] = smartPageData[key];

      return;
    }

    siteConfig.websiteSourceData[wsdKey] = smartPageData[key];

    if (wsdKey === WEBSITE_SOURCE_DATA_KEY_MAPPING.CallToActions) {
      siteConfig.websiteSourceData.CallToActions =
        cloneDeep(siteConfig.websiteSourceData.CallToActions);
      convertCallToActionId(siteConfig.websiteSourceData.CallToActions);
    }
  }
};

// Enrich this config as need
const WSDFieldConfig = {
  BusinessName: {
    getter: siteConfig => getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.BusinessName),
  },
  Description: {
    getter: (siteConfig) => {
      const [businessDescription, businessCategories] = getDataSourceByKeys(siteConfig, [
        DATA_SOURCE_KEY_MAPPING.BusinessDescription,
        DATA_SOURCE_KEY_MAPPING.BusinessCategories,
      ]);
      const descriptionFromCategory = businessCategories && businessCategories.join(', ');

      return businessDescription || descriptionFromCategory || '';
    },
  },
  Introduction: {
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.Introduction
    ),
  },
  Categories: {
    getter: siteConfig => getDataSourceByKey(
      siteConfig,
      DATA_SOURCE_KEY_MAPPING.BusinessCategories
    ),
  },
  SingleLineAddress: {
    getter: siteConfig => getDataSourceByKey(
      siteConfig,
      DATA_SOURCE_KEY_MAPPING.LocationInfo
    ),
  },
  PhoneNumber: {
    getter: siteConfig => getDataSourceByKey(
      siteConfig,
      DATA_SOURCE_KEY_MAPPING.PhoneNumber
    ),
  },
  CoverImageUrl: {
    getter: (siteConfig) => {
      const coverImageInfo = getComponentByType(siteConfig, DATA_SOURCE_KEY_MAPPING.CoverImage);

      return (coverImageInfo && coverImageInfo.imageId)
        ? getImageUrlById(siteConfig, coverImageInfo.imageId)
        : siteConfig.localImageUrl;
    },
  },
  CoverImageId: {
    getter: (siteConfig) => {
      const coverImageInfo = getComponentByType(siteConfig, DATA_SOURCE_KEY_MAPPING.CoverImage);

      return coverImageInfo && coverImageInfo.imageId;
    },
  },
  LogoImageId: {
    getter: siteConfig => getDataSourceByKey(
      siteConfig,
      DATA_SOURCE_KEY_MAPPING.LogoImageId
    ),
  },
  LogoImageUrl: {
    getter: (siteConfig) => {
      const logoImageId = getDataSourceByKey(
        siteConfig,
        DATA_SOURCE_KEY_MAPPING.LogoImageId
      );

      return getImageUrlById(siteConfig, logoImageId);
    },
  },
  GalleryImages: {
    getter: (siteConfig) => {
      const galleryImageInfo = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.Gallery);
      const galleryPhotos = galleryImageInfo ? galleryImageInfo.photos : [];
      const galleryImageIds = _.map(galleryPhotos, photo => photo.imageId);

      return _.chain(galleryImageIds)
        .map(imageId => getImageUrlById(siteConfig, imageId))
        .filter(item => !!item)
        .value();
    },
  },
  GalleryImageIds: {
    getter: (siteConfig) => {
      const galleryImageInfo = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.Gallery);
      const galleryPhotos = galleryImageInfo ? galleryImageInfo.photos : [];

      return _.map(galleryPhotos, photo => photo.imageId);
    },
  },
  Subdomain: {
    getter: siteConfig => (siteConfig.subDomain || generateGuid()),
  },
  Email: {
    getter: (siteConfig) => {
      const emails = getDataSourceByKey(
        siteConfig,
        DATA_SOURCE_KEY_MAPPING.Emails
      );

      return emails && emails[0];
    },
  },
  SocialUrls: {
    getter: (siteConfig) => {
      const socialLinksInfo = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.SocialLinks);
      const socialLinks = _.keys(socialLinksInfo).reduce((acc, key) => {
        if (linkNameToModel[key] && socialLinksInfo[key].isDisplay) {
          const linkName = linkNameToModel[key];
          acc[linkName] = socialLinksInfo[key].value;
        }
        return acc;
      }, {});

      return socialLinks;
    },
  },
  BusinessHours: {
    getter: (siteConfig, pilots) => {
      // When business hours componment is available, read data from it,
      // otherwise read data from Contact.
      const businessHoursSourceInfo = getComponentByType(
        siteConfig,
        pilots[COMPONENT_TYPE_MAPPING.BusinessHours]
          ? COMPONENT_TYPE_MAPPING.BusinessHours
          : COMPONENT_TYPE_MAPPING.Contact
      );
      const businessHoursInfo = businessHoursSourceInfo
        ? businessHoursSourceInfo.businessHours
        : [];

      return mapHoursToWSDFormat(businessHoursInfo);
    },
  },
  CallToActions: {
    getter: (siteConfig) => {
      const callToActions = getDataSourceByKey(
        siteConfig,
        DATA_SOURCE_KEY_MAPPING.CallToActions,
        [],
        false
      );
      // OData model uses Id, so we have to convert Id to CallToActionId.
      convertCallToActionId(callToActions);

      return callToActions;
    },
  },
  EmbeddedVideos: {
    defaultValue: [],
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.EmbeddedVideos
    ),
  },
  Locations: {
    defaultValue: [],
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.Locations
    ),
  },
  Theme: {
    defaultValue: {
      Mode: 'Light',
      AccentColor: 'Default',
    },
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.Theme
    ),
  },
  Services: {
    defaultValue: [],
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.Services
    ),
  },
  Appointment: {
    defaultValue: {},
    getter: (siteConfig) => {
      const appointment = getWebsiteSourceDataByKey(
        siteConfig,
        WEBSITE_SOURCE_DATA_KEY_MAPPING.Appointment
      ) || {};
      const CTAConfig = getComponentByType(
        siteConfig,
        COMPONENT_TYPE_MAPPING.CallToAction
      ) || {};

      if (
        CTAConfig.actionType === 'BookAppointment'
        && CTAConfig.isExternalLink === false
        && _.isEmpty(appointment.AppointmentHours)
      ) {
        // Business Hours may store in two places: Business Hours Component and Contact Component.
        const bizHourConfig = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.BusinessHours);
        const contactConfig = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.Contact);
        let businessHours = _.propertyOf(bizHourConfig)(DATA_SOURCE_KEY_MAPPING.BusinessHours);
        if (_.isEmpty(bizHourConfig)) {
          businessHours = _.propertyOf(contactConfig)(DATA_SOURCE_KEY_MAPPING.BusinessHours);
        }

        let fallbackHours = getDefaultHoursConfig();
        if (!_.isEmpty(businessHours)) {
          fallbackHours = businessHours;
        }

        // Filter out "Holidays", not support holidays now.
        fallbackHours = _.filter(fallbackHours, item => item.day !== 'holiday');

        return {
          AppointmentHours: mapHoursToWSDFormat(fallbackHours),
          Services: [],
        };
      }

      return appointment;
    },
  },
  Reviews: {
    defaultValue: [],
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.Reviews
    ),
  },
  TimeZoneId: {
    defaultValue: 71,
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.TimeZoneId
    ),
  },
  PrivacyPolicy: {
    defaultValue: '',
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.PrivacyPolicy
    ),
  },
  Localization: {
    defaultValue: '',
    getter: siteConfig => getWebsiteSourceDataByKey(
      siteConfig,
      WEBSITE_SOURCE_DATA_KEY_MAPPING.Localization
    ),
  },
};

export const createWebsiteSourceData = (siteConfig, serverContext = {}) => {
  const componentsPilotMapping = getComponentsPilotMapping(serverContext);

  return _.mapObject(WSDFieldConfig, (val) => {
    // The third parameter can treat as "this", in case we want to reuse some calculation logic
    // inside the "getter", like using "businessHours" inside the "appointments".
    const value = val.getter(siteConfig, componentsPilotMapping, WSDFieldConfig);
    return value || val.defaultValue;
  });
};
